/**
 * The "Or start from a brief →" step: one searchable dropdown listing the briefs
 * A→Z, instead of a button per brief. (POR-14700)
 *
 * No imports, so the page and the store can both use it without a cycle.
 */

export const BRIEF_PICKER_WIDGET_TYPE = 'brief_picker';

// The "Or start from a brief →" button under the "What product…?" question.
const PRODUCT_ASK_WIDGET_ID = 'pdp_from_brief';

// Case-insensitive, and numbers compare as numbers ("test 2" before "test 10").
const titleCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * @param {Array<{id: number|string, title?: string, number?: number|null}>} briefs
 * @returns {Array<{value: string, label: string, number: number|null}>}
 */
export function briefPickerOptions(briefs) {
  return (briefs || [])
    .filter((b) => b && b.id != null)
    .map((b) => ({
      value: String(b.id),
      label: String(b.title || '').trim() || `Brief ${b.id}`,
      // Shown next to the title: many briefs share a name ("Untitled brief").
      number: b.number != null ? Number(b.number) : null,
    }))
    .sort((a, b) => titleCollator.compare(a.label, b.label) || (a.number || 0) - (b.number || 0));
}

/** The message's brief picker widget, or null. */
export function briefPickerWidget(message) {
  return ((message && message.widgets) || []).find((w) => w && w.type === BRIEF_PICKER_WIDGET_TYPE) || null;
}

/**
 * True for a step of the brief flow the user can still act on: the brief picker
 * (also once a brief is loaded, to switch to another) and the "What product…?"
 * question with its "Or start from a brief →" button. Moving on closes them.
 */
export function isOpenBriefStep(message) {
  return !!message
    && !message.disabled
    && (message.widgets || []).some((w) => w && (w.type === BRIEF_PICKER_WIDGET_TYPE || w.id === PRODUCT_ASK_WIDGET_ID));
}

/** parsedPrompt without the brief a pick staged on it (see stageBriefInCreativeStudio). */
export function withoutStagedBrief(parsedPrompt) {
  const prompt = parsedPrompt || {};
  return {
    ...prompt,
    activeBriefId: null,
    activeBriefTitle: '',
    pdp_brief_size: null,
    pdp_brief_product_name: null,
    // The product the brief brought goes with it; one the user gave stays. (POR-14794)
    product_references: (prompt.product_references || []).filter((r) => !r || r._source !== 'brief_product'),
  };
}
